<template>
  <v-dialog v-model="dialog" max-width="900" scrollable>
    <template v-slot:activator="{ on: dialogEvents, attrs }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltipEvents }">
          <v-btn
            icon
            color="primary"
            aria-label="Cum sunt calculați rivalii principali"
            v-bind="attrs"
            v-on="Object.assign({}, dialogEvents, tooltipEvents)"
          >
            <v-icon size="28">mdi-help-circle</v-icon>
          </v-btn>
        </template>
        <span>Cum sunt calculați rivalii?</span>
      </v-tooltip>
    </template>

    <v-card>
      <v-card-title class="d-flex flex-nowrap align-start">
        <div>
          <div class="text-h5">Cum sunt determinați rivalii principali?</div>
          <div class="text-subtitle-2 text--secondary mt-1">
            Metodologia versiunii beta
          </div>
        </div>
        <v-spacer />
        <v-btn icon aria-label="Închide explicațiile" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="rivalry-help-content py-5">
        <p>
          Funcția identifică <strong>apropierea competitivă</strong>, nu doar cei mai
          rapizi sau mai puternici adversari. Calculele sunt făcute separat pentru
          fiecare simulator.
        </p>

        <h3>1. Date eligibile</h3>
        <p>
          Sunt luate in calcul ultimele șase etape la care pilotul a
          participat, numai din sezoane marcate <code>rivalryEligible</code>. Sunt
          analizate doar sesiunile oficiale de cursă și calificari. O competiție
          neeligibilă poate influența doar predicția propriei etape viitoare.
        </p>

        <h3>2. Apropierea în CURSE — 50%</h3>
        <p>Fiecare cursă oficială comparabilă primește următoarea valoare:</p>
        <v-simple-table dense class="mb-3">
          <thead>
            <tr>
              <th>Diferență la poziția finală</th>
              <th>Valoare de apropiere</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>0–1 poziții</td><td>100%</td></tr>
            <tr><td>2 poziții</td><td>75%</td></tr>
            <tr><td>3 poziții</td><td>50%</td></tr>
            <tr><td>4 poziții</td><td>25%</td></tr>
            <tr><td>5 sau mai multe</td><td>0%</td></tr>
          </tbody>
        </v-simple-table>
        <ul>
          <li>Se folosește poziția finală din clasă; poziția generală este fallback.</li>
          <li>Penalizările sunt deja reflectate în poziția finală și nu sunt punctate separat.</li>
          <li>Rezultatele inutilizabile sau descalificarile sunt excluse.</li>
          <li>Piloții sunt comparați în aceeași clasă, atunci când această informație există.</li>
          <li>Colegii din aceeași mașină nu sunt comparați între ei.</li>
          <li>Rezultatele obținute în mașini împărțite, au o pondere redusă la 65%.</li>
          <li>Cursele multiple din aceeași etapă sunt mediate într-o singură contribuție.</li>
          <li>Toate etapele comune intră în medie, inclusiv rezultatele îndepărtate cu valoare zero.</li>
          <li>O etapă este „apropiată” dacă cel puțin o cursă a avut o diferență de maximum patru poziții.</li>
        </ul>
        <p>
          Etapele recente cântăresc mai mult. Ponderile, de la cea mai nouă la cea
          mai veche, sunt <code>1,00 · 0,85 · 0,70 · 0,55 · 0,40 · 0,25</code>.
        </p>

        <h3>3. Scorul direct</h3>
        <p>
          Primul număr arată în câte curse pilotul din profil a terminat înaintea
          rivalului; al doilea arată situația inversă. Fiecare cursă dintr-o etapă
          se numără separat. Scorul nu reprezintă victorii sau depășiri și este
          afișat după minimum trei curse comparabile.
        </p>
        <p>
          Echilibrul rezultatului ajustează apropierea în curse cu un factor între
          85% și 100%. Un scor perfect echilibrat păstrează valoarea maximă; unul
          unilateral o reduce cu cel mult 15%. Scorul direct nu poate crea singur
          o rivalitate.
        </p>

        <h3>4. Apropierea în clasamentul relevant — 25%</h3>
        <p>
          Sunt folosite numai clasamentele individuale, publice și oficiale din
          sezoane eligibile. Are prioritate cel mai recent sezon curent. Dacă nu
          există un clasament curent utilizabil, poate fi folosit numai cel mai
          recent sezon încheiat care apare în fereastra de șase etape a pilotului.
          Se folosește consecvent clasamentul general al aceleiași categorii auto,
          indiferent de clasa piloților. În competițiile multiclasă, fiecare categorie
          auto reprezintă un context separat. Clasamentele PRO/AM nu sunt amestecate
          în calcul, deoarece punctajele lor nu sunt direct comparabile.
        </p>
        <p>
          Intervalul relevant este maximul dintre 10 puncte și 25% din punctele
          liderului. Apropierea scade liniar până la zero la capătul intervalului.
          O diferență mai mare rămâne zero. Dacă datele există, dar contribuția este
          zero, criteriul apare în secțiunea „Date de control beta”.
        </p>
        <p>
          Ambii piloți trebuie să apară în același clasament și să aibă minimum două
          participări sau un total relevant de puncte. Scorul este redus dacă totalul
          oricăruia este aproape de zero.
        </p>
        <p class="formula">apropiere = max(0, 1 − diferența de puncte / interval)</p>

        <h3>5. Apropierea de ritm — 25%</h3>
        <p>
          Pentru etapele încheiate sunt folosite tururile valide atribuite piloților
          din calificările oficiale ale acelorași șase etape. Diferențele sunt
          transformate într-un indice:
        </p>
        <p class="formula">apropiere = max(0, 1 − diferența procentuală / 1,5)</p>
        <v-simple-table dense class="mb-3">
          <thead>
            <tr>
              <th>Diferență de timp</th>
              <th>Indice de apropiere</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>0%</td><td>100%</td></tr>
            <tr><td>0,75%</td><td>50%</td></tr>
            <tr><td>1,5% sau mai mult</td><td>0%</td></tr>
          </tbody>
        </v-simple-table>
        <p>
          Și aici etapele recente cântăresc mai mult. Cardul arată numărul probelor,
          indicele agregat folosit efectiv și cine a fost mai rapid în medie. Tooltip-ul
          indicelui păstrează și cea mai mică diferență individuală.
        </p>
        <p>
          Dacă ambii piloți au o precalificare comparabilă pentru o etapă viitoare,
          aceasta are prioritate față de istoricul calificărilor.
        </p>

        <h3>6. Relevanța etapei viitoare</h3>
        <p>
          Dacă ambii piloți sunt confirmați la aceeași etapă viitoare și intrările
          lor sunt comparabile după clasă sau model, relevanța finală primește un
          multiplicator de <code>×1,25</code>. Acesta se poate aplica și unei
          competiții neeligibile pentru istoricul persistent.
        </p>
        <p>
          Dacă există mai multe etape viitoare, cea mai puternică apropiere de ritm
          disponibilă este folosită în calcul.
        </p>

        <h3>7. Afinitate, încredere și date lipsă</h3>
        <p>Afinitatea de bază este:</p>
        <p class="formula">50% curse + 25% clasament + 25% ritm</p>
        <p>
          Datele lipsă nu sunt considerate rezultate slabe și ponderile lor nu sunt
          redistribuite. Încrederea crește odată cu numărul etapelor apropiate și cu
          acoperirea criteriilor disponibile.
        </p>
        <p class="formula">
          încredere = min(1, etape apropiate / 3) × (0,70 + 0,30 × acoperire)
        </p>
        <p>
          Acoperirea pornește de la 50% pentru curse și adaugă câte 25% atunci când
          există date de clasament și de ritm.
        </p>
        <ul>
          <li>Cel puțin trei etape apropiate: „Rivalitate actuală”.</li>
          <li>Două etape apropiate: „Rivalitate posibilă”.</li>
          <li>Mai puțin istoric: poate apărea numai o predicție pentru etapa următoare.</li>
        </ul>

        <h3>8. Rivalul specific etapei următoare</h3>
        <p>
          Un pilot fără suficient istoric poate apărea dacă ambii sunt confirmați la
          aceeași etapă și precalificările comparabile indică un ritm apropiat. Pragul
          corespunde aproximativ unei diferențe de cel mult 1%.
        </p>
        <p class="formula">încredere etapă = 0,45 + 0,15 × apropierea de ritm curentă</p>

        <h3>9. Ordinea finală</h3>
        <p class="formula">relevanță = afinitate × încredere × multiplicatorul etapei viitoare</p>
        <p>
          Rezultatele sub pragul minim de <code>0,08</code> sunt eliminate. Restul sunt ordonate separat
          pentru fiecare simulator și sunt afișați maximum trei: Rival #1, #2 și #3.
          Indicii sunt măsuri normalizate de apropiere, nu probabilități de duel.
        </p>

        <h3>10. Ce nu este luat în calcul</h3>
        <ul>
          <li>depășiri, incidente sau vinovăția stabilită de comisari;</li>
          <li>penalizări ca factor independent;</li>
          <li>victorii considerate izolat sau un clasament general de tip Elo;</li>
          <li>clasamente istorice care nu mai sunt curente;</li>
          <li>istoricul competițiilor distractive marcate ca neeligibile;</li>
          <li>tururi de antrenament sau cursă neatribuite ca înlocuitor pentru calificări.</li>
        </ul>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn text color="primary" @click="dialog = false">Închide</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'RivalryHelpDialog',
  data() {
    return {
      dialog: false
    }
  }
}
</script>

<style scoped>
.rivalry-help-content h3 {
  margin: 24px 0 8px;
}

.rivalry-help-content li {
  margin-bottom: 6px;
}

.formula {
  padding: 10px 12px;
  border-left: 3px solid var(--v-primary-base);
  background: rgba(127, 127, 127, 0.08);
  font-family: monospace;
}
</style>
